import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Download, CheckCircle, Trash2, Loader2, WifiOff } from "lucide-react";
import { toast } from "sonner";
import {
  downloadLessonForOffline,
  getLessonDownload,
  removeLessonDownload,
} from "../lib/offlineCache";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OfflineDownloadButton — user-facing "Download for Offline" control for a single lesson.
 *
 * States:
 *   idle       → blue "Download for Offline" button
 *   downloading→ disabled "Saving… (label)"
 *   saved      → emerald "Available Offline" pill + "Remove" chip
 *   error      → amber "Couldn't save — try again"
 *
 * Metrics (via /api/metrics/event):
 *   offline_download_clicked    when the user taps the main action
 *   offline_download_completed  when the save finishes successfully
 *   offline_opened              emitted by the parent Lesson page when the lesson
 *                               renders while the user is offline OR while a saved
 *                               copy exists — this component only fires the
 *                               download events.
 */
export default function OfflineDownloadButton({ lessonId, compact = false }) {
  const [state, setState] = useState("checking"); // checking | idle | downloading | saved | error
  const [progress, setProgress] = useState("");

  const refresh = useCallback(async () => {
    const rec = await getLessonDownload(lessonId);
    if (rec) {
      setState("saved");
    } else {
      setState("idle");
    }
  }, [lessonId]);

  useEffect(() => { refresh(); }, [refresh]);

  const trackEvent = (event) => {
    axios.post(`${API}/api/metrics/event`, {
      event,
      role: "learner",
      testid: "offline-download",
      metadata: { lesson_id: lessonId },
    }, { withCredentials: true }).catch(() => {});
  };

  const handleDownload = async () => {
    if (state === "downloading") return;
    trackEvent("offline_download_clicked");
    setState("downloading");
    setProgress("preparing");
    try {
      await downloadLessonForOffline(lessonId, {
        onProgress: (label) => setProgress(label),
      });
      setState("saved");
      toast.success("Saved for later use without signal.", { duration: 3500 });
      trackEvent("offline_download_completed");
    } catch (e) {
      setState("error");
      const isQuota = (e && (e.name === "QuotaExceededError" ||
        (e.message || "").toLowerCase().includes("quota")));
      toast.error(
        isQuota
          ? "Your device is out of space for downloads."
          : "Couldn't save the lesson — check your connection and try again."
      );
    } finally {
      setProgress("");
    }
  };

  const handleRemove = async () => {
    await removeLessonDownload(lessonId);
    setState("idle");
    toast.success("Download removed — it's no longer taking up space.");
  };

  if (state === "checking") return null;

  // ── SAVED ──
  if (state === "saved") {
    return (
      <div
        data-testid="offline-saved-card"
        className={`rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/15 ${compact ? "p-2.5" : "p-3.5"}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
              Available Offline
              <WifiOff className="w-3 h-3 opacity-60" />
            </p>
            <p className="text-[11px] text-emerald-700 dark:text-emerald-300/80 mt-0.5">
              Saved for later use without signal.
            </p>
          </div>
          <button
            data-testid="offline-remove-btn"
            onClick={handleRemove}
            className="flex items-center gap-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 dark:hover:text-emerald-100 px-2 py-1 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/30 flex-shrink-0"
            aria-label="Remove offline download"
          >
            <Trash2 className="w-3 h-3" />
            Remove
          </button>
        </div>
      </div>
    );
  }

  // ── DOWNLOADING ──
  if (state === "downloading") {
    const niceLabel = {
      preparing: "Saving…",
      "fetching-lesson": "Saving text…",
      "fetching-audio": "Saving audio…",
    }[progress] || (progress.startsWith("fetching-image") ? "Saving images…" : "Saving…");

    return (
      <button
        data-testid="offline-downloading-btn"
        disabled
        className={`w-full rounded-2xl border border-blue-200 bg-blue-50 dark:bg-blue-900/15 dark:border-blue-900/40 ${compact ? "p-2.5" : "p-3.5"} flex items-center gap-3`}
      >
        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Loader2 className="w-4 h-4 text-white animate-spin" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-extrabold text-blue-900 dark:text-blue-200">{niceLabel}</p>
          <p className="text-[11px] text-blue-700 dark:text-blue-300/80 mt-0.5">
            Hang on — won't take long.
          </p>
        </div>
      </button>
    );
  }

  // ── ERROR ──
  if (state === "error") {
    return (
      <button
        data-testid="offline-retry-btn"
        onClick={handleDownload}
        className={`w-full rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-900/40 ${compact ? "p-2.5" : "p-3.5"} flex items-center gap-3`}
      >
        <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
          <Download className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-extrabold text-amber-900 dark:text-amber-200">Couldn't save — try again</p>
          <p className="text-[11px] text-amber-700 dark:text-amber-300/80 mt-0.5">
            Check your signal, then tap to retry.
          </p>
        </div>
      </button>
    );
  }

  // ── IDLE ──
  return (
    <button
      data-testid="offline-download-btn"
      onClick={handleDownload}
      className={`w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 ${compact ? "p-2.5" : "p-3.5"} flex items-center gap-3 hover:border-blue-300 dark:hover:border-blue-700 active:scale-[0.99] transition-all`}
    >
      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
        <Download className="w-4 h-4 text-slate-600 dark:text-slate-300" />
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-extrabold text-slate-900 dark:text-white">Download for Offline</p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
          Saved for later use without signal.
        </p>
      </div>
    </button>
  );
}
